import React from 'react';
import { FormattedNumber } from 'react-intl';
import { useTranslate } from '../../mixins/translate/translate';
import { Roles } from '../../utils/enums/Roles';
import {
    PanelBody,
    PanelHeading,
    PanelLink,
    PanelWithoutForm,
    PopupElement,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../elements';
import { IMaintenanceMeasure } from '../interfaces/IMaintenanceMeasure';
import { MaintenancePopupDetails } from '../MaintenanceMeasures/MaintenancePopupDetails';
import { Spinner } from '../spinners';
import { ConditionScoreLabel } from '../utils/ConditionScoreLabel';
import { FmecaRiskLabel } from '../utils/FmecaRiskLabel';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { RamsWeightedPrioLabel } from '../utils/RamsWeightedPrioLabel';
import RoleDependent from '../utils/RoleDependent';

function FormatCondition(props: {
    score?: number;
    ramsWeightedPriority?: string;
    analysisRamsWeightedPriority?: string;
    verificationRamsWeightedPriority?: string;
}) {
    if (props === undefined) {
        return null;
    }
    if (props.ramsWeightedPriority && props.ramsWeightedPriority !== '0') {
        return <RamsWeightedPrioLabel value={props.ramsWeightedPriority} />;
    }
    if (props.verificationRamsWeightedPriority && props.verificationRamsWeightedPriority !== '0') {
        return <FmecaRiskLabel weightedValue={props.verificationRamsWeightedPriority} />;
    }
    if (props.analysisRamsWeightedPriority && props.analysisRamsWeightedPriority !== '0') {
        return <FmecaRiskLabel weightedValue={props.analysisRamsWeightedPriority} />;
    }
    if (props.score) {
        return <ConditionScoreLabel value={props.score} />;
    }
    return null;
}

export function ObjectMaintenanceList(props: {
    objectId: string;
    isLoading: boolean;
    maintenanceMeasures: IMaintenanceMeasure[];
    setImplementationYear: (maintenanceMeasureId: string) => void;
}) {
    const translate = useTranslate();

    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedMaintenance, setSelectedMaintenance] = React.useState(null as IMaintenanceMeasure | null);

    return (
        <>
            <PanelWithoutForm id="Maintenance">
                <PanelHeading text={translate('maintenanceMeasure.openMaintenanceMeasures')} icon="fa fa-wrench">
                    <PanelLink
                        href={`/api/objects/${props.objectId}/miokExcel`}
                        text="MJOP export"
                        isDownload={true}
                        icon="fa fa-download"
                        small={true}
                    />
                </PanelHeading>
                <PanelBody noPadding={true} whiteBackground={true}>
                    <Table hover={true}>
                        <TableHead>
                            <tr className="bg-light">
                                <TableHeader>{translate('common.location')}</TableHeader>
                                <TableHeader>{translate('maintenanceMeasure.maintenanceMeasure')}</TableHeader>
                                <TableHeader>{translate('common.cost')}</TableHeader>
                                <TableHeader>{translate('maintenanceMeasure.planned')}</TableHeader>
                                <TableHeader>{translate('maintenanceMeasure.completed')}</TableHeader>
                                <TableHeader>{translate('survey.surveyedOn')}</TableHeader>
                                <TableHeader align="right">
                                    {translate('common.condition')}&nbsp;/&nbsp;{translate('failureMode.risk')}
                                </TableHeader>
                                <RoleDependent allowed={[Roles.Administrator]} content={<TableHeader>&nbsp;</TableHeader>} />
                            </tr>
                        </TableHead>

                        {(props.isLoading && (
                            <TableBody>
                                <TableRow>
                                    <TableData colSpan={7}>
                                        <Spinner />
                                    </TableData>
                                </TableRow>
                            </TableBody>
                        )) || (
                            <TableBody>
                                {props.maintenanceMeasures.map((maintenance) => (
                                    <TableRow
                                        key={maintenance.id}
                                        onClick={() => {
                                            setSelectedMaintenance(maintenance);
                                            setIsOpen(!isOpen);
                                        }}
                                    >
                                        {maintenance.unit && (
                                            <TableData>
                                                <span className="fs12">
                                                    {maintenance.unit.element.name}
                                                    <br />
                                                    {maintenance.unit.name}
                                                </span>
                                            </TableData>
                                        )}
                                        {maintenance.manifestation && (
                                            <TableData>
                                                <span className="fs12">
                                                    {maintenance.manifestation.unit.element.name}
                                                    <br />
                                                    {maintenance.manifestation.unit.name}
                                                    <br />
                                                    {maintenance.manifestation.name}
                                                </span>
                                            </TableData>
                                        )}
                                        <TableData>
                                            {maintenance.description}
                                            {(maintenance.maintenanceType && (
                                                <div className="fs12 text-muted-darker">{maintenance.maintenanceType} </div>
                                            )) || (
                                                <div className="fs12 text-muted-darker">{translate('maintenanceMeasure.correctiveMaintenance')} </div>
                                            )}
                                        </TableData>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <FormattedNumber value={maintenance.cost ?? 0} style="currency" currency="EUR" />{' '}
                                        </td>
                                        <td className="fs12" style={{ whiteSpace: 'nowrap' }}>
                                            {maintenance.planYear} {maintenance.finalPlanYear && <span>-{maintenance.finalPlanYear}</span>}
                                        </td>
                                        <td className="fs12">{maintenance.implementationYear}</td>
                                        <TableData>
                                            {maintenance.survey && <NullableFormattedDate value={maintenance.survey.surveryedOn} />}
                                        </TableData>
                                        <TableData alignRight={true}>
                                            <FormatCondition {...{ ...maintenance.defect, ...maintenance.failureMode }}></FormatCondition>
                                        </TableData>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </PanelBody>
            </PanelWithoutForm>

            {isOpen && selectedMaintenance && (
                <>
                    <PopupElement onClick={() => setIsOpen(!isOpen)}>
                        <MaintenancePopupDetails
                            maintenanceMeasure={selectedMaintenance}
                            setImplementationYear={props.setImplementationYear}
                            onClose={() => setIsOpen(false)}
                        />
                    </PopupElement>
                </>
            )}
        </>
    );
}
